import React, { useEffect, useState, useCallback } from 'react';
import { DebounceInput } from 'react-debounce-input';
import Card from '../common/Card';
import Table from '../common/Table';
import Loading from '../common/Loading';
import AssetDetailsModal from './AssetDetailsModal';
import { getAllAssetsForClient, getPublicAssetCategories, getAssetHistory } from '../../api/assetsApi';
import { getAllAvailableTenants } from '../../api/tenantsApi';
import '../../assets/css/allAssetsCard.css';

function AllAssetsCard() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [take, setTake] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    assetStatus: '',
    assetCategoryId: '',
    tenantId: '',
    search: '',
  });

  const [assetCategories, setAssetCategories] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const SEARCH_DEBOUNCE_MS = 400;

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const categoriesResponse = await getPublicAssetCategories();
        setAssetCategories(categoriesResponse.data);

        const tenantsResponse = await getAllAvailableTenants();
        setTenants(tenantsResponse.data);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };

    fetchFilterOptions();
  }, []);

  const fetchAssets = useCallback(
    async (pageValue = page, takeValue = take, currentFilters = filters) => {
      try {
        setLoading(true);
        const skip = pageValue * takeValue;

        // Filter parameters, will only be included if they have a value (cleaner payload to the backend)
        // Query from the searchbar is trimmed and any multiple spaces are replaced with a single space
        const query = (currentFilters.search || '').trim().replace(/\s+/g, ' ');
        const filterParams = {
          skip,
          take: takeValue,
          ...(currentFilters.assetStatus && { assetStatus: parseInt(currentFilters.assetStatus) }),
          ...(currentFilters.assetCategoryId && { assetCategoryId: currentFilters.assetCategoryId }),
          ...(currentFilters.tenantId && { tenantId: currentFilters.tenantId }),
          ...(query && { filter: query }),
        };

        const response = await getAllAssetsForClient(filterParams);
        setAssets(response.data.data);
        setTotalCount(response.data.totalCount);
      } catch (err) {
        setError('Failed to load assets');
      } finally {
        setLoading(false);
      }
    },
    [page, take],
  );

  useEffect(() => {
    fetchAssets(page, take, filters);
  }, [fetchAssets, page, take]);

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    setPage(0);
    fetchAssets(0, take, newFilters);
  };

  const handleSearchChange = (value) => {
    const newFilters = { ...filters, search: value };
    setFilters(newFilters);
    setPage(0);
    fetchAssets(0, take, newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      assetStatus: '',
      assetCategoryId: '',
      tenantId: '',
      search: '',
    };
    setFilters(clearedFilters);
    setPage(0);
    fetchAssets(0, take, clearedFilters);
  };

  const handleChangePage = (_, pageIndex) => {
    setPage(pageIndex);
    fetchAssets(pageIndex, take, filters);
  };

  const handleChangeRowsPerPage = (event) => {
    const newTake = event.target.value;
    setTake(newTake);
    setPage(0);
    fetchAssets(0, newTake, filters);
  };

  const handleAssetClick = async (asset) => {
    setSelectedAsset(asset);
    setShowAssetModal(true);
    setLoadingHistory(true);

    try {
      const historyResponse = await getAssetHistory(asset.assetId);
      const historyData = historyResponse.data || {};
      setSelectedAsset((prev) => ({
        ...prev,
        history: historyData.data || [],
        historyTotalCount: historyData.totalCount ?? (historyData.data ? historyData.data.length : 0),
      }));
    } catch (error) {
      console.error('Failed to load asset history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCloseAssetModal = () => {
    setShowAssetModal(false);
    setSelectedAsset(null);
    setLoadingHistory(false);
  };

  const columns = (
    <>
      <th className="col-lg-6">Asset</th>
      <th>Asset Category</th>
      <th>Asset Tenant</th>
      <th className="text-right">Availability</th>
    </>
  );

  const getStatusClass = (statusNumber) => {
    switch (statusNumber) {
      case 0:
        return 'badge badge-success';
      case 1:
        return 'badge badge-info';
      case 2:
        return 'badge badge-warning';
      case 3:
        return 'badge badge-danger';
      case 4:
        return 'badge badge-primary';
      default:
        return 'badge badge-secondary';
    }
  };

  const getStatusDisplayName = (statusNumber) => {
    switch (statusNumber) {
      case 0:
        return 'Available';
      case 1:
        return 'Spare';
      case 2:
        return 'In Repair';
      case 3:
        return 'Unavailable';
      case 4:
        return 'In Service';
      default:
        return 'Unknown';
    }
  };

  const mappedAssets = assets.map((asset, i) => (
    <tr key={i} className="clickable" style={{ cursor: 'pointer' }} onClick={() => handleAssetClick(asset)}>
      <td>{asset.name}</td>
      <td>{asset.assetCategory?.name || 'TBC'}</td>
      <td>{asset.tenant?.name || 'TBC'}</td>
      <td className="text-right">
        <span className={getStatusClass(asset.assetStatus)}>{getStatusDisplayName(asset.assetStatus)}</span>
      </td>
    </tr>
  ));

  const filterControls = (
    <div className="filters-box">
      <div className="row g-1 filters-row justify-content-start">
        <div className="col-auto">
          <label className="form-label-sm">Category</label>
          <select className="form-control filter-control" value={filters.assetCategoryId} onChange={(e) => handleFilterChange('assetCategoryId', e.target.value)}>
            <option value="">All Types</option>
            {assetCategories.map((category) => (
              <option key={category.assetCategoryId} value={category.assetCategoryId}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-auto">
          <label className="form-label-sm">Tenant</label>
          <select className="form-control filter-control" value={filters.tenantId} onChange={(e) => handleFilterChange('tenantId', e.target.value)}>
            <option value="">All</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-auto">
          <label className="form-label-sm">Status</label>
          <select className="form-control filter-control" value={filters.assetStatus} onChange={(e) => handleFilterChange('assetStatus', e.target.value)}>
            <option value="">All</option>
            <option value="0">Available</option>
            <option value="1">Spare</option>
            <option value="2">In Repair</option>
            <option value="3">Unavailable</option>
            <option value="4">In Service</option>
          </select>
        </div>

        {/* Empty div to create space between filter options and right-side controls */}
        <div className="col-auto flex-grow-1"></div>

        <div className="col-auto search-box">
          <label className="form-label-sm">Search</label>
          <DebounceInput
            element="input"
            type="text"
            className="form-control filter-control"
            placeholder="Search assets..."
            value={filters.search}
            debounceTimeout={SEARCH_DEBOUNCE_MS}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        <div className="col-auto d-flex align-items-end justify-content-end pe-0">
          <button type="button" className="btn btn-sm clear-btn" onClick={clearFilters}>
            Clear
          </button>
        </div>
      </div>
    </div>
  );

  if (error) {
    return (
      <Card title="All Available Assets" icon="pe-7s-car" iconGradient="bg-green-blue-top">
        <div className="text-center mt-5">
          <p className="text-danger">{error}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="All Available Assets" icon="pe-7s-car" iconGradient="bg-green-blue-top" contentClass="card-table-content">
      <div className="filters-top-spacer" />
      {filterControls}
      <div className="filters-bottom-spacer" />
      {loading && (
        <div className="mb-2">
          <Loading message="Loading assets..." />
        </div>
      )}
      {assets.length === 0 ? (
        <div className="text-center mt-5">
          <h4>No assets found</h4>
          <p style={{ color: '#999' }}>
            {Object.values(filters).some((f) => f !== '') ? 'No assets match your current filters.' : 'There are no assets available at the moment.'}
          </p>
        </div>
      ) : (
        <Table
          columns={columns}
          data={mappedAssets}
          showPagination={true}
          totalCount={totalCount}
          skip={page}
          take={take}
          changePageHandler={handleChangePage}
          changeRowsPerPageHandler={handleChangeRowsPerPage}
        />
      )}

      <AssetDetailsModal show={showAssetModal} onClose={handleCloseAssetModal} asset={selectedAsset} loadingHistory={loadingHistory} />
    </Card>
  );
}

export default AllAssetsCard;

